/**
 * WLOU Mod Tracker page.
 *
 * At-a-glance dashboard of every mod in the server's repack + whether the
 * Steam Workshop copy is newer than the packed one. Row colouring:
 *   red   → Steam Last Updated > Last Repacked  ("needs update")
 *   green → Steam Last Updated ≤ Last Repacked  ("current")
 *   grey  → no Steam date (private/removed/unfilled)
 *
 * Access is gated server-side; the page also honours `capabilities.can_edit`
 * (upload / refresh / edit Workshop ID + Load order) and
 * `capabilities.can_edit_status` (testers → status dropdown only).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ZipReader, BlobReader, TextWriter, configure } from "@zip.js/zip.js";

// Zomboid repacks are routinely 2-4 GB. Tell zip.js to stream the reads
// rather than buffering the whole file into an ArrayBuffer (which trips
// Chrome's 2 GB Blob limit and produces the confusing "requested file
// could not be read" error).
configure({ useWebWorkers: true });
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, useSortable,
  sortableKeyboardCoordinates, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ManualBackup from "./modtracker/ManualBackup";
import {
  Loader2,
  ShieldAlert,
  AlertTriangle,
  Upload,
  RefreshCw,
  Wand2,
  Copy,
  ExternalLink,
  Save,
  Trash2,
  Package,
  Pencil,
  GripVertical,
  Undo2,
  FlaskConical,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { useDiscord } from "@/context/DiscordContext";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const STATUS_STYLES = {
  // Untested = red chip so it stands out among green/yellow — testers spot
  // the work queue at a glance.
  NOT_TESTED: "border-[#E27B7B] text-[#E27B7B]",
  TESTED: "border-[#F5D97A] text-[#F5D97A]",
  IMPLEMENTED: "border-[#4ADE80] text-[#4ADE80]",
};

const CONFIDENCE_STYLES = {
  high: { color: "#4ADE80", label: "high match" },
  medium: { color: "#F5D97A", label: "medium — verify" },
  low: { color: "#E27B7B", label: "low — please verify" },
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "numeric", day: "numeric" });
}

// Client-side mod.info search order — mirrors the server-side `_MOD_INFO_SUFFIXES`.
// Root wins over `42/` wins over `common/` for dual-compat packs.
// mod.info can appear in several places inside a Zomboid pack. WLOU is a
// Build 42 server, so we always prefer a B42 flavour over anything else.
// A mod with both `mod.info` (usually the legacy B41 fallback) and
// `42/mod.info` or `42.0/mod.info` (the B42 file) MUST resolve to the B42
// one — otherwise we track the wrong mod_id and the server tries to load
// a mod that isn't there.
//
// Priority order:
//   1. `42/mod.info`                — B42 explicit
//   2. `42.<n>/mod.info`            — B42 build-version (42.0, 42.5, …)
//                                     highest-sorted number wins so newer
//                                     builds beat older ones
//   3. any other `42<x>/mod.info`   — defensive: catches `42x/`, `42beta/`
//   4. `common/mod.info`            — dual-compat (safe for either build)
//   5. `mod.info`                   — root (legacy B41)
//   6. any other `<subdir>/mod.info` — fallback for exotic layouts
const NESTED_MOD_INFO_RE = /^[^/]+\/mod\.info$/;
const B42_SUBDIR_RE = /^42(?:[.\-_]\S*)?\/mod\.info$/i;

function isModInfoSuffix(tail) {
  return (
    tail === "mod.info"
    || tail === "common/mod.info"
    || B42_SUBDIR_RE.test(tail)
    || NESTED_MOD_INFO_RE.test(tail)
  );
}

// Given every mod.info found under one mod folder, pick which one to parse.
// See the priority list above for the full ordering.
function _pickModInfo(entries) {
  // 1. Exact `42/mod.info`
  const b42Exact = entries.find((e) => e.tail === "42/mod.info");
  if (b42Exact) return b42Exact;

  // 2 + 3. B42 build-version folders (42.0, 42.5, 42x, 42-beta, …) — pick
  // the highest-sorted name so a mod shipping both `42.0/` and `42.5/`
  // uses the newer build.
  const b42Nested = entries
    .filter((e) => B42_SUBDIR_RE.test(e.tail))
    .sort((a, b) => b.tail.localeCompare(a.tail, undefined, { numeric: true }));
  if (b42Nested.length) return b42Nested[0];

  // 4. common/
  const common = entries.find((e) => e.tail === "common/mod.info");
  if (common) return common;

  // 5. root
  const root = entries.find((e) => e.tail === "mod.info");
  if (root) return root;

  // 6. any other single-level `<subdir>/mod.info` (usually B41 build folders
  // like `41.78/`). Highest-sorted still wins for consistency.
  const other = entries
    .filter((e) => NESTED_MOD_INFO_RE.test(e.tail))
    .sort((a, b) => b.tail.localeCompare(a.tail, undefined, { numeric: true }));
  return other[0] || null;
}

/**
 * Parse a Zomboid repack zip in the browser and return a lightweight manifest
 * [{mod_id, mod_name, last_repacked, mod_folder, folder_mtime, maps}] that
 * the server can merge.
 *
 * Uses @zip.js/zip.js because JSZip has to buffer the entire file into an
 * ArrayBuffer before it can read the central directory — which fails on
 * Chrome for files ≥ 2 GB (Blob limit). @zip.js/zip.js streams the reads
 * so a 4 GB pack lists its central directory in milliseconds and only the
 * few dozen mod.info files (a few KB each) are actually pulled from disk.
 *
 * We only send a JSON manifest (< 100 KB) to the server, never the pack
 * itself — that also side-steps any cloud ingress body-size cap.
 *
 * `onProgress(phase, percent)` is called during long steps so the UI can
 * show "Reading pack (37%)…". Phases: "reading" | "parsing" | "sending".
 */
async function buildManifestFromZip(file, onProgress) {
  onProgress?.("reading", 0);
  // BlobReader streams the read directly from the File handle — never
  // materialises the whole zip in memory.
  const zipReader = new ZipReader(new BlobReader(file));
  let entries;
  try {
    entries = await zipReader.getEntries();
  } catch (err) {
    await zipReader.close();
    throw err;
  }

  // Group entries by mod-folder prefix. We compute mtime across ALL files in
  // the folder so a repack that only touched a single texture still moves
  // last_repacked forward. Also sum the uncompressed byte sizes — that
  // becomes the single most reliable "am I in sync with Steam?" signal
  // because Steam publishes `file_size` too and bytes don't lie.
  const folders = new Map(); // prefix → { infoEntries, mtime, maps, folder, size }
  for (const entry of entries) {
    if (entry.directory) continue;
    const normalised = entry.filename.replace(/\\/g, "/");
    const m = normalised.match(/^(?:.*?\/)?Contents\/mods\/([^/]+)\/(.+)$/);
    if (!m) continue;
    const folderName = m[1];
    const tail = m[2];
    const prefixRoot = normalised.slice(0, normalised.length - tail.length);
    let slot = folders.get(prefixRoot);
    if (!slot) {
      slot = { infoEntries: [], mtime: null, maps: new Set(), folder: folderName, size: 0 };
      folders.set(prefixRoot, slot);
    }
    // zip.js surfaces the zip entry's date on `entry.lastModDate`.
    const d = entry.lastModDate instanceof Date ? entry.lastModDate : null;
    if (d && !Number.isNaN(d.getTime()) && (!slot.mtime || d > slot.mtime)) slot.mtime = d;
    if (typeof entry.uncompressedSize === "number") slot.size += entry.uncompressedSize;
    if (isModInfoSuffix(tail)) slot.infoEntries.push({ tail, entry });

    // Any file under `media/maps/<mapfolder>/…` (any build-version prefix)
    // contributes the <mapfolder> name to that mod's map list.
    const mapMatch = tail.match(/^(?:[^/]+\/)?media\/maps\/([^/]+)\//);
    if (mapMatch) slot.maps.add(mapMatch[1]);
  }

  const mods = [];
  const prefixes = Array.from(folders.keys());
  try {
    for (let i = 0; i < prefixes.length; i++) {
      const prefix = prefixes[i];
      const slot = folders.get(prefix);
      if (!slot.infoEntries.length) continue;

      const chosen = _pickModInfo(slot.infoEntries);
      if (!chosen) continue;

      // Streamed read of just this one mod.info blob.
      const text = await chosen.entry.getData(new TextWriter());
      const fields = {};
      for (const rawLine of text.split(/\r?\n/)) {
        const line = rawLine.trim();
        if (!line || line.startsWith("#")) continue;
        const eq = line.indexOf("=");
        if (eq < 0) continue;
        const k = line.slice(0, eq).trim().toLowerCase();
        const v = line.slice(eq + 1).trim();
        if (!(k in fields)) fields[k] = v;
      }
      const modId = (fields.id || "").trim();
      if (!modId) continue;
      const modName = (fields.name || "").trim() || modId;
      const modVersion = (fields.modversion || fields.version || "").trim();
      // We prefer the `mod.info` file's own mtime over the folder-wide max.
      // Steam Workshop always rewrites mod.info on every version bump, but
      // OneDrive / AV / integrity checks routinely touch textures without
      // touching mod.info — those spurious touches would otherwise fake a
      // recent Last Repacked and hide a real "needs update".
      const infoMtime = chosen.entry.lastModDate instanceof Date && !Number.isNaN(chosen.entry.lastModDate.getTime())
        ? chosen.entry.lastModDate
        : null;
      const primaryMtime = infoMtime || slot.mtime || null;
      mods.push({
        mod_id: modId,
        mod_name: modName,
        mod_version: modVersion,
        // Total uncompressed bytes for every file in this mod folder — the
        // most reliable "am I in sync with Steam?" signal because Steam
        // publishes `file_size` too, and bytes don't lie the way mtimes do.
        total_size: slot.size,
        last_repacked: primaryMtime ? primaryMtime.toISOString() : new Date().toISOString(),
        folder_mtime: primaryMtime ? primaryMtime.toISOString() : null,
        // Ship both signals so the admin (and the UI tooltip) can see the
        // difference — "mod.info touched X but textures touched Y".
        mod_info_mtime: infoMtime ? infoMtime.toISOString() : null,
        folder_max_mtime: slot.mtime ? slot.mtime.toISOString() : null,
        mod_folder: slot.folder,
        maps: Array.from(slot.maps).sort(),
      });

      if (onProgress) onProgress("parsing", Math.round(((i + 1) / prefixes.length) * 100));
    }
  } finally {
    await zipReader.close();
  }
  return mods;
}

function formatDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function rowStatusFor(mod) {
  const localSize = mod.total_size;
  const steamSize = mod.steam_file_size;

  // Positive-only byte check: if local uncompressed size matches Steam's
  // file_size exactly, we know we're synced (strong positive signal). If
  // they DIFFER we can't conclude anything — Steam ships mods in its own
  // packaged format and the byte totals rarely match even when the mod
  // is up to date (compression, metadata files, filesystem overhead).
  // So a size mismatch just falls through to the mtime comparison.
  if (typeof localSize === "number" && typeof steamSize === "number" &&
      localSize > 0 && steamSize > 0 && localSize === steamSize) {
    return "current";
  }

  const steamRaw = mod.steam_last_updated;
  const packedRaw = mod.last_repacked;
  if (!steamRaw) return "no-steam-date";
  const steam = new Date(steamRaw).getTime();
  const packed = new Date(packedRaw || 0).getTime();
  if (!Number.isFinite(steam)) return "no-steam-date";
  return steam > packed ? "needs-update" : "current";
}

// Human-readable bytes for the Last Repacked tooltip. Kept local so we
// don't pull a full formatter dep.
function formatBytes(n) {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return null;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

export default function ModTrackerPage() {
  return (
    <div className="App min-h-screen bg-[#0D110C]">
      <Navbar />
      <main className="pt-[72px]">
        <ModTrackerBody />
      </main>
      <Footer />
    </div>
  );
}

function ModTrackerBody() {
  const { loaded, isConnected, isAdmin, user, connect } = useDiscord();
  const [state, setState] = useState(null); // { workshop_url, mods, ... , capabilities }
  const [loadingState, setLoadingState] = useState("idle"); // idle | loading | error | denied
  const [errorMsg, setErrorMsg] = useState("");

  const load = useCallback(async () => {
    setLoadingState("loading");
    setErrorMsg("");
    try {
      const res = await api.get("/mod-tracker");
      setState(res.data);
      setLoadingState("idle");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setLoadingState("denied");
      } else {
        setErrorMsg(err?.response?.data?.detail || err.message || "Failed to load.");
        setLoadingState("error");
      }
    }
  }, []);

  useEffect(() => {
    if (loaded && isConnected) load();
  }, [loaded, isConnected, load]);

  if (!loaded) {
    return (
      <CenterCard>
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={28} />
        <p className="mt-4 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">Loading session…</p>
      </CenterCard>
    );
  }

  if (!isConnected) {
    return (
      <CenterCard>
        <ShieldAlert className="text-[#C0A875] mx-auto" size={36} />
        <h2 className="mt-4 font-display uppercase text-[#E2E4DF] text-3xl">Sign in required</h2>
        <p className="mt-2 text-[#8D9A87] text-sm">Connect Discord to view the Mod Tracker.</p>
        <button
          data-testid="mod-tracker-connect"
          onClick={connect}
          className="mt-6 bg-[#5865F2] hover:bg-[#4752C4] text-white px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em]"
        >
          Connect Discord
        </button>
      </CenterCard>
    );
  }

  if (!isAdmin || loadingState === "denied") {
    return (
      <CenterCard>
        <ShieldAlert className="text-[#C0392B] mx-auto" size={36} />
        <h2 className="mt-4 font-display uppercase text-[#E2E4DF] text-3xl">Access denied</h2>
        <p className="mt-2 text-[#8D9A87] text-sm max-w-md mx-auto">
          Signed in as <span className="text-[#C0A875]">@{user?.username}</span>. This account isn't on the mod-tracker
          access list. Ask a super-admin to add you as staff with the <code className="text-[#C0A875]">mod_tracker_edit</code>{" "}
          or <code className="text-[#C0A875]">mod_tracker_tester</code> permission.
        </p>
        <Link
          to="/admin"
          className="mt-6 inline-block border border-[#5C6B51] hover:border-[#C0A875] text-[#E2E4DF] hover:text-[#C0A875] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em]"
        >
          ← Admin console
        </Link>
      </CenterCard>
    );
  }

  if (loadingState === "loading" && !state) {
    return (
      <CenterCard>
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={28} />
      </CenterCard>
    );
  }

  if (loadingState === "error") {
    return (
      <CenterCard>
        <p className="text-[#E27B7B] font-mono-ui text-sm">Error: {errorMsg}</p>
        <button
          onClick={load}
          className="mt-4 border border-[#5C6B51] hover:border-[#C0A875] text-[#E2E4DF] px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.25em]"
        >
          Retry
        </button>
      </CenterCard>
    );
  }

  if (!state) return null;

  return <ModTrackerContent state={state} setState={setState} reload={load} />;
}

function ModTrackerContent({ state, setState, reload }) {
  const { packs, base_maps, statuses, update_check_states: updateCheckStates, capabilities } = state;
  const canEdit = !!capabilities?.can_edit;
  const canEditStatus = !!capabilities?.can_edit_status;

  // Which pack tab is active. Persist across reloads so the user lands back
  // in the pack they were editing.
  const [selectedPackId, setSelectedPackId] = useState(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem("mt.selectedPackId") : null;
    if (saved && (packs || []).some((p) => p.id === saved)) return saved;
    return (packs || [])[0]?.id || null;
  });
  useEffect(() => {
    // Keep the selection valid when packs list changes (add/delete/rename).
    if (!packs || !packs.length) return;
    if (!packs.find((p) => p.id === selectedPackId)) {
      setSelectedPackId(packs[0].id);
    }
  }, [packs, selectedPackId]);
  useEffect(() => {
    if (selectedPackId && typeof window !== "undefined") {
      window.localStorage.setItem("mt.selectedPackId", selectedPackId);
    }
  }, [selectedPackId]);

  // Whether the dev-server load-order section is expanded. Persist so
  // an admin who prefers to keep it collapsed sees it that way every time.
  const [devLoaderOpen, setDevLoaderOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem("mt.devLoaderOpen");
    return saved === null ? true : saved === "1";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mt.devLoaderOpen", devLoaderOpen ? "1" : "0");
    }
  }, [devLoaderOpen]);

  const activePack = useMemo(
    () => (packs || []).find((p) => p.id === selectedPackId) || (packs || [])[0] || null,
    [packs, selectedPackId]
  );
  const packQuery = activePack?.id ? `?pack_id=${encodeURIComponent(activePack.id)}` : "";

  const workshop_url = activePack?.workshop_url || "";
  const description = activePack?.description || "";
  const mods = activePack?.mods || [];
  const last_upload_at = activePack?.last_upload_at;
  const last_refresh_at = activePack?.last_refresh_at;

  const [busy, setBusy] = useState(null); // "upload" | "refresh" | "autofill" | null
  const [uploadProgress, setUploadProgress] = useState(null); // { phase, percent } | null
  const [urlDraft, setUrlDraft] = useState(workshop_url || "");
  const [urlDirty, setUrlDirty] = useState(false);
  const [descDraft, setDescDraft] = useState(description || "");
  const [descDirty, setDescDirty] = useState(false);
  const [baseMapsDraft, setBaseMapsDraft] = useState(base_maps || "");
  const [baseMapsDirty, setBaseMapsDirty] = useState(false);
  // Persistent operation error so the user isn't reliant on a bottom-right
  // toast (which can be missed or covered by the MusicPlayer bar).
  const [opError, setOpError] = useState(null);

  const reportError = useCallback((label, err) => {
    // Log the full axios error to DevTools so the user (or the agent, later)
    // can inspect status codes / response bodies when diagnosing.
    // eslint-disable-next-line no-console
    console.error(`[Mod Tracker] ${label} failed:`, err, err?.response);
    const raw = err?.response?.data?.detail;
    let detail;
    if (typeof raw === "string") {
      detail = raw;
    } else if (Array.isArray(raw)) {
      // FastAPI validation errors → array of {loc,msg,type}
      detail = raw.map((e) => e?.msg || JSON.stringify(e)).join("; ");
    } else if (raw) {
      detail = JSON.stringify(raw);
    } else {
      detail = err?.response?.statusText || err?.message || "Unknown error";
    }
    const status = err?.response?.status;
    const msg = status ? `${label} failed (${status}): ${detail}` : `${label} failed: ${detail}`;
    setOpError(msg);
    toast.error(msg);
  }, []);

  useEffect(() => {
    setUrlDraft(workshop_url || "");
    setUrlDirty(false);
  }, [workshop_url]);

  useEffect(() => {
    setDescDraft(description || "");
    setDescDirty(false);
  }, [description]);

  useEffect(() => {
    setBaseMapsDraft(base_maps || "");
    setBaseMapsDirty(false);
  }, [base_maps]);

  const sortedMods = useMemo(
    () => [...(mods || [])].sort((a, b) => (a.load_order || 0) - (b.load_order || 0)),
    [mods]
  );

  const counts = useMemo(() => {
    const c = { needsUpdate: 0, current: 0, noSteam: 0 };
    for (const m of sortedMods) {
      const s = rowStatusFor(m);
      if (s === "needs-update") c.needsUpdate += 1;
      else if (s === "current") c.current += 1;
      else c.noSteam += 1;
    }
    return c;
  }, [sortedMods]);

  // The Mods= and Map= copy lines concatenate every pack (Pack 1 → Pack N)
  // so the server config gets a single paste-able load order regardless of
  // how the admin split mods across tabs. Dev packs (flagged for the
  // development / testers server) get their OWN copy lines below so the
  // production Mods= doesn't accidentally include experimental mods.
  const flatten = (predicate) => {
    const out = [];
    for (const p of packs || []) {
      if (!predicate(p)) continue;
      const mm = [...(p.mods || [])].sort(
        (a, b) => (a.load_order || 0) - (b.load_order || 0)
      );
      for (const m of mm) out.push({ mod: m, pack: p });
    }
    return out;
  };

  const prodPacks = useMemo(
    () => (packs || []).filter((p) => !p.exclude_from_duplicates),
    [packs]
  );
  const devPacks = useMemo(
    () => (packs || []).filter((p) => p.exclude_from_duplicates),
    [packs]
  );

  const prodSortedByPack = useMemo(
    () => flatten((p) => !p.exclude_from_duplicates),
    [packs]
  );
  const devSortedByPack = useMemo(
    () => flatten((p) => !!p.exclude_from_duplicates),
    [packs]
  );
  // Kept as an alias so existing rowStatusFor / other consumers that
  // iterated the whole set still see everything if anything relies on it.
  const allSortedByPack = useMemo(
    () => [...prodSortedByPack, ...devSortedByPack],
    [prodSortedByPack, devSortedByPack]
  );

  const buildLoadOrderString = (source) => {
    const seen = new Set();
    const ids = [];
    for (const { mod: m } of source) {
      if (m.enabled === false) continue;
      const id = (m.mod_id || "").trim();
      if (!id) continue;
      // Dedupe within the same target — a mod present in two packs
      // would otherwise double-load and either crash Zomboid or waste
      // memory. Prod vs. dev sets are computed independently.
      const k = id.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      ids.push(id);
    }
    return `Mods=${ids.join(";")}`;
  };

  const buildMapString = (source, includeBaseMaps) => {
    const seen = new Set();
    const parts = [];
    for (const { mod: m } of source) {
      if (m.enabled === false) continue;
      for (const mp of m.maps || []) {
        const v = (mp || "").trim();
        if (!v || seen.has(v.toLowerCase())) continue;
        seen.add(v.toLowerCase());
        parts.push(v);
      }
    }
    if (includeBaseMaps) {
      // Base maps (e.g. "Muldraugh, KY;West Point, KY") — semicolon-
      // separated, trimmed. Accept newlines too since the input is a
      // textarea. Only append to the production Map= line — dev packs
      // pick their own base maps if they need them.
      for (const raw of (baseMapsDraft || "").split(/[;\n]/)) {
        const v = raw.trim();
        if (!v || seen.has(v.toLowerCase())) continue;
        seen.add(v.toLowerCase());
        parts.push(v);
      }
    }
    return `Map=${parts.join(";")}`;
  };

  const loadOrderString = useMemo(
    () => buildLoadOrderString(prodSortedByPack),
    [prodSortedByPack]
  );
  const mapString = useMemo(
    () => buildMapString(prodSortedByPack, true),
    [prodSortedByPack, baseMapsDraft]
  );
  const workshopString = useMemo(() => {
    const seen = new Set();
    const ids = [];
    for (const { mod: m } of prodSortedByPack) {
      const id = String(m.workshop_id || "").trim();
      if (m.enabled === false || !id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    return `WorkshopItems=${ids.join(";")}`;
  }, [prodSortedByPack]);
  const devLoadOrderString = useMemo(
    () => buildLoadOrderString(devSortedByPack),
    [devSortedByPack]
  );
  const devMapString = useMemo(
    () => buildMapString(devSortedByPack, false),
    [devSortedByPack]
  );

  const mapCount = useMemo(() => {
    // Count for the card header — mirrors what will actually land in the Map= line.
    return mapString === "Map=" ? 0 : mapString.slice(4).split(";").length;
  }, [mapString]);
  const devMapCount = useMemo(() => {
    return devMapString === "Map=" ? 0 : devMapString.slice(4).split(";").length;
  }, [devMapString]);

  const enabledCount = useMemo(
    () => prodSortedByPack.filter(({ mod }) => mod.enabled !== false && (mod.mod_id || "").trim()).length,
    [prodSortedByPack]
  );
  const devEnabledCount = useMemo(
    () => devSortedByPack.filter(({ mod }) => mod.enabled !== false && (mod.mod_id || "").trim()).length,
    [devSortedByPack]
  );

  // Cross-pack duplicate mods. A mod_id that appears in ≥2 production
  // packs will be silently de-duped in the `Mods=` copy string (first
  // occurrence wins), but the admin needs a heads-up so they can decide
  // which pack really owns that mod — otherwise inconsistent
  // status/enabled flags across packs will confuse the load order they
  // see vs the one that ships. Packs marked `exclude_from_duplicates`
  // (dev / testers packs) are intentionally allowed to share mods with
  // the production packs and are skipped by this check.
  const duplicates = useMemo(() => {
    // mod_id (lowercased) → [{ packId, packName, enabled }, ...]
    const byId = new Map();
    for (const p of packs || []) {
      if (p.exclude_from_duplicates) continue;
      for (const m of p.mods || []) {
        const id = (m.mod_id || "").trim();
        if (!id) continue;
        const k = id.toLowerCase();
        if (!byId.has(k)) byId.set(k, { id, occurrences: [] });
        byId.get(k).occurrences.push({
          packId: p.id,
          packName: p.name,
          enabled: m.enabled !== false,
        });
      }
    }
    return Array.from(byId.values()).filter((entry) => entry.occurrences.length > 1);
  }, [packs]);

  // Fast lookup for row-level "In N packs" chip on the currently rendered pack.
  const duplicateModIds = useMemo(() => {
    const s = new Set();
    for (const d of duplicates) s.add(d.id.toLowerCase());
    return s;
  }, [duplicates]);

  const saveUrl = async () => {
    setBusy("settings");
    setOpError(null);
    try {
      const res = await api.put(`/admin/mod-tracker/settings${packQuery}`, { workshop_url: urlDraft });
      setState(res.data);
      setUrlDirty(false);
      toast.success("Workshop URL saved.");
    } catch (err) {
      reportError("Save URL", err);
    } finally {
      setBusy(null);
    }
  };

  const saveDescription = async () => {
    if (!activePack?.id) return;
    setBusy("settings");
    setOpError(null);
    try {
      const res = await api.patch(
        `/admin/mod-tracker/packs/${encodeURIComponent(activePack.id)}`,
        { description: descDraft }
      );
      setState(res.data);
      setDescDirty(false);
      toast.success("Pack description saved.");
    } catch (err) {
      reportError("Save description", err);
    } finally {
      setBusy(null);
    }
  };

  const saveBaseMaps = async () => {
    setBusy("settings");
    setOpError(null);
    try {
      // Send workshop_url too so the backend model doesn't blank it (it's
      // required-ish — empty string is fine but null would drop the value).
      const res = await api.put(`/admin/mod-tracker/settings${packQuery}`, {
        workshop_url: workshop_url || "",
        base_maps: baseMapsDraft,
      });
      setState(res.data);
      setBaseMapsDirty(false);
      toast.success("Base maps saved.");
    } catch (err) {
      reportError("Save base maps", err);
    } finally {
      setBusy(null);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please upload a .zip file.");
      return;
    }
    setBusy("upload");
    setOpError(null);
    setUploadProgress({ phase: "reading", percent: 0 });
    try {
      // Parse the zip locally so we never push hundreds of megabytes to the
      // server (cloud ingresses cap request bodies well below a real repack).
      const manifest = await buildManifestFromZip(file, (phase, percent) => {
        setUploadProgress({ phase, percent });
      });
      if (!manifest.length) {
        throw new Error(
          "Couldn't find any Contents/mods/<mod>/mod.info entries in that zip. Double-check it's a Zomboid repack."
        );
      }
      setUploadProgress({ phase: "sending", percent: 100 });
      // Post the tiny JSON manifest (a few KB even for 100 mods).
      const res = await api.post(`/admin/mod-tracker/upload/manifest${packQuery}`, { mods: manifest });
      setState(res.data);
      const newActive = (res.data.packs || []).find((p) => p.id === activePack?.id) || (res.data.packs || [])[0];
      const modCount = (newActive?.mods || []).length;
      // Surface the add/remove diff so the admin can immediately confirm
      // the upload matched their intent. Common footgun caught here:
      // "I removed a mod from the pack but the tracker still shows it" —
      // if `removed` is empty for that mod, the zip is still shipping it.
      const diff = res.data.upload_diff || { added: [], removed: [], kept: 0 };
      const addedTxt = diff.added.length ? `+${diff.added.length}` : "";
      const removedTxt = diff.removed.length ? `−${diff.removed.length}` : "";
      const diffTxt = [addedTxt, removedTxt].filter(Boolean).join(" ");
      toast.success(
        `${newActive?.name || "Pack"}: ${modCount} mods (${diffTxt || "no changes"}) · ${(file.size / (1024 * 1024)).toFixed(0)} MB scanned locally.`
      );
      // Extra info toast when mods were dropped — lists up to 5 by name so
      // the admin can eyeball whether the right ones came out.
      if (diff.removed.length) {
        const names = diff.removed.slice(0, 5).map((m) => m.mod_name || m.mod_id).join(", ");
        const more = diff.removed.length > 5 ? ` +${diff.removed.length - 5} more` : "";
        toast(`Removed from pack: ${names}${more}`, { duration: 8000 });
      }
      // If admin thinks a mod was removed but the diff says nothing was
      // removed, either the zip still contains that mod (leftover backup
      // folder) or the mod they removed was already gone. Log the diff
      // to devtools so admins can inspect the exact IDs.
      // eslint-disable-next-line no-console
      console.info("[Mod Tracker] Upload diff", diff);
    } catch (err) {
      // JSZip throws plain Errors; axios throws AxiosErrors. Both flow here.
      reportError("Upload", err);
    } finally {
      setBusy(null);
      setUploadProgress(null);
    }
  };

  const handleRefresh = async () => {
    setBusy("refresh");
    setOpError(null);
    try {
      const res = await api.post(`/admin/mod-tracker/refresh${packQuery}`, {});
      setState(res.data);
      toast.success("Steam Workshop refresh complete.");
    } catch (err) {
      reportError("Refresh", err);
    } finally {
      setBusy(null);
    }
  };

  const handleAutofill = async () => {
    setBusy("autofill");
    setOpError(null);
    try {
      const res = await api.post(`/admin/mod-tracker/autofill-workshop-ids${packQuery}`, {});
      setState(res.data);
      if (res.data.note) {
        toast(res.data.note);
      } else {
        const via = res.data.method === "search" ? " via Workshop search" : " from your collection";
        toast.success(
          `Auto-filled ${res.data.matched} of ${res.data.total_children} Workshop IDs${via}. Verify medium/low confidence dots.`
        );
      }
    } catch (err) {
      reportError("Auto-fill", err);
    } finally {
      setBusy(null);
    }
  };

  const patchMod = async (modId, body) => {
    // Optimistic update so inline edits feel snappy — patch only within the
    // active pack.
    setState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        packs: (prev.packs || []).map((p) =>
          p.id !== activePack?.id
            ? p
            : {
                ...p,
                mods: (p.mods || []).map((m) =>
                  m.mod_id === modId
                    ? {
                        ...m,
                        ...body,
                        workshop_confidence:
                          body.workshop_id !== undefined ? null : m.workshop_confidence,
                      }
                    : m
                ),
              }
        ),
      };
    });
    try {
      const res = await api.patch(
        `/admin/mod-tracker/mods/${encodeURIComponent(modId)}${packQuery}`,
        body
      );
      setState(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
      // Reload authoritative state
      reload();
    }
  };

  const deleteMod = async (modId) => {
    if (!window.confirm(`Remove "${modId}" from ${activePack?.name || "this pack"}?`)) return;
    try {
      const res = await api.delete(`/admin/mod-tracker/mods/${encodeURIComponent(modId)}${packQuery}`);
      setState(res.data);
      toast.success("Mod removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  // ------- Bulk selection for the Update Check column -------
  // A Set of mod_id strings selected in the *currently active pack*. We
  // reset on tab change so a Pack 1 selection doesn't leak into Pack 2.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedPackId]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelected = useCallback((modId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(modId)) next.delete(modId);
      else next.add(modId);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const bulkApplyUpdateCheck = async (value) => {
    if (!canEditStatus || selectedIds.size === 0 || bulkBusy) return;
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    // Optimistic: paint every selected row with the new value in one setState.
    setState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        packs: (prev.packs || []).map((p) =>
          p.id !== activePack?.id
            ? p
            : {
                ...p,
                mods: (p.mods || []).map((m) =>
                  selectedIds.has(m.mod_id) ? { ...m, update_check: value } : m
                ),
              }
        ),
      };
    });
    try {
      // Single atomic bulk write — reads the doc once, modifies every listed
      // row, writes once. Avoids the "N parallel PATCHes race and clobber
      // each other on the shared singleton doc" bug that ate updates when
      // more than one row was selected.
      const res = await api.patch(
        `/admin/mod-tracker/mods/bulk${packQuery}`,
        { mod_ids: ids, update_check: value }
      );
      // Server returns the full authoritative tracker payload — swap it in
      // directly so we don't need a second GET round-trip.
      const { updated: _updated, ...tracker } = res.data || {};
      setState(tracker);
      toast.success(
        `${ids.length} mod${ids.length === 1 ? "" : "s"} → ${
          UPDATE_CHECK_LABELS[value] || value || "cleared"
        }.`
      );
      setSelectedIds(new Set());
    } catch (err) {
      reportError("Bulk Update Check", err);
      // Fall back to a fresh authoritative reload so the optimistic paint
      // doesn't lie to the admin about server state.
      await reload();
    } finally {
      setBulkBusy(false);
    }
  };

  // Derived: are all/some rows in the active pack currently selected?
  // Only counts rows the admin can act on (with a mod_id).
  const selectableIds = useMemo(
    () => sortedMods.map((m) => m.mod_id).filter(Boolean),
    [sortedMods]
  );
  const selectedCount = selectedIds.size;
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someSelected = selectedCount > 0 && !allSelected;
  const toggleSelectAll = () => {
    if (allSelected || someSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableIds));
    }
  };

  // Drag-and-drop reorder. `arrayMove` produces the new order client-side;
  // we optimistic-update immediately, POST the fresh ordering to the server,
  // and let the response replace state (server re-numbers 1..N).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderMods = useCallback(async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const nextOrder = arrayMove(sortedMods, fromIndex, toIndex);
    // Optimistic: renumber the local rows so the UI reflects the drop
    // before the server round-trip.
    setState((prev) => {
      if (!prev) return prev;
      const orderedIds = nextOrder.map((m) => m.mod_id);
      return {
        ...prev,
        packs: (prev.packs || []).map((p) =>
          p.id !== activePack?.id
            ? p
            : {
                ...p,
                mods: (p.mods || []).map((m) => {
                  const idx = orderedIds.indexOf(m.mod_id);
                  return idx >= 0 ? { ...m, load_order: idx + 1 } : m;
                }),
              }
        ),
      };
    });
    try {
      const res = await api.put(`/admin/mod-tracker/reorder${packQuery}`, {
        mod_ids: nextOrder.map((m) => m.mod_id),
      });
      setState(res.data);
    } catch (err) {
      reportError("Reorder", err);
      reload();
    }
  }, [sortedMods, setState, reportError, reload, packQuery, activePack]);

  // ---- Pack CRUD ----

  const createPack = useCallback(async () => {
    try {
      const res = await api.post("/admin/mod-tracker/packs", { name: `Pack ${(packs || []).length + 1}` });
      setState(res.data);
      const created = (res.data.packs || []).find(
        (p) => !(packs || []).some((old) => old.id === p.id)
      );
      if (created) setSelectedPackId(created.id);
      toast.success("Pack added.");
    } catch (err) {
      reportError("Create pack", err);
    }
  }, [packs, setState, reportError]);

  const renamePack = useCallback(async (packId, newName) => {
    const trimmed = (newName || "").trim();
    if (!trimmed) return;
    try {
      const res = await api.patch(`/admin/mod-tracker/packs/${encodeURIComponent(packId)}`, { name: trimmed });
      setState(res.data);
    } catch (err) {
      reportError("Rename pack", err);
    }
  }, [setState, reportError]);

  const toggleDevPack = useCallback(async (packId) => {
    // Flip the "excluded from cross-pack duplicate check" flag. Used for
    // dev / testers packs whose mods intentionally overlap with the
    // production packs (Pack 1-4) so the duplicate banner shouldn't
    // flag them as conflicts.
    const target = (packs || []).find((p) => p.id === packId);
    if (!target) return;
    const next = !target.exclude_from_duplicates;
    try {
      const res = await api.patch(
        `/admin/mod-tracker/packs/${encodeURIComponent(packId)}`,
        { exclude_from_duplicates: next }
      );
      setState(res.data);
      toast.success(
        next
          ? `"${target.name}" marked as dev pack — excluded from duplicate check.`
          : `"${target.name}" back to production — included in duplicate check.`
      );
    } catch (err) {
      reportError("Toggle dev pack", err);
    }
  }, [packs, setState, reportError]);

  const deletePack = useCallback(async (packId) => {
    const target = (packs || []).find((p) => p.id === packId);
    if (!target) return;
    if (!window.confirm(`Delete "${target.name}" and all ${(target.mods || []).length} mods inside it? This can't be undone.`)) return;
    try {
      const res = await api.delete(`/admin/mod-tracker/packs/${encodeURIComponent(packId)}`);
      setState(res.data);
      // Fall back to the first remaining pack.
      const firstId = (res.data.packs || [])[0]?.id;
      if (firstId) setSelectedPackId(firstId);
      toast.success("Pack deleted.");
    } catch (err) {
      reportError("Delete pack", err);
    }
  }, [packs, setState, reportError]);

  const reorderPacks = useCallback(async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const nextOrder = arrayMove(packs || [], fromIndex, toIndex);
    // Optimistic swap.
    setState((prev) => (prev ? { ...prev, packs: nextOrder } : prev));
    try {
      const res = await api.put("/admin/mod-tracker/packs/reorder", {
        pack_ids: nextOrder.map((p) => p.id),
      });
      setState(res.data);
    } catch (err) {
      reportError("Reorder packs", err);
      reload();
    }
  }, [packs, setState, reportError, reload]);

  const undoUpload = useCallback(async () => {
    // Restore the pack's mods array from the last-upload snapshot. Used
    // when the admin realises they picked the wrong tab and hit upload.
    // Guarded by confirm() so a stray click doesn't nuke a good upload.
    if (!activePack?.id || !activePack.undo_available) return;
    const modCount = activePack.undo_mod_count || 0;
    if (!window.confirm(
      `Undo the last upload to "${activePack.name}"? The pack will roll back to its previous state (${modCount} mod${modCount === 1 ? "" : "s"}). This can't be re-undone.`
    )) return;
    try {
      const res = await api.post(`/admin/mod-tracker/undo-upload${packQuery}`, {});
      setState(res.data);
      toast.success(`Undo complete — "${activePack.name}" restored.`);
    } catch (err) {
      reportError("Undo upload", err);
    }
  }, [activePack, packQuery, setState, reportError]);

  const copyText = useCallback(async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied.`);
    } catch {
      // Fallback for browsers without async clipboard.
      const el = document.createElement("textarea");
      el.value = value;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
        toast.success(`${label} copied.`);
      } finally {
        document.body.removeChild(el);
      }
    }
  }, []);

  return (
    <section className="py-14 sm:py-20 border-t border-[#283524]">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <span className="font-mono-ui text-[10px] uppercase tracking-[0.35em] text-[#8D9A87]">
              // Pack ops
            </span>
            <h1 className="mt-3 font-display uppercase text-[#E2E4DF] text-4xl sm:text-5xl flex items-center gap-3">
              <Package className="text-[#C0A875]" size={38} />
              WLOU Mod Tracker
            </h1>
            <p className="mt-3 text-[#8D9A87] text-sm font-mono-ui max-w-3xl">
              {last_upload_at ? (
                <>
                  <span className="text-[#C0A875]">{activePack?.name || "Current pack"}</span> uploaded{" "}
                  <span className="text-[#C0A875]">{formatDateTime(last_upload_at)}</span>.
                </>
              ) : (
                <>Upload a repack .zip to <span className="text-[#C0A875]">{activePack?.name || "this pack"}</span> to start tracking mods.</>
              )}{" "}
              {last_refresh_at && (
                <>
                  Steam checked <span className="text-[#C0A875]">{formatDateTime(last_refresh_at)}</span>.
                </>
              )}
            </p>
            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-2">
              <LegendChip color="#8B1A1A" text="Needs update" count={counts.needsUpdate} testid="legend-needs-update" />
              <LegendChip color="#2E4A2A" text="Current" count={counts.current} testid="legend-current" />
              <LegendChip color="#3A3A3A" text="No Steam date" count={counts.noSteam} testid="legend-no-steam" />
            </div>
          </div>

          {canEdit && (
            <div className="flex flex-wrap gap-2 items-center">
              <ToolbarButton
                icon={Upload}
                label={
                  busy === "upload"
                    ? uploadProgress
                      ? uploadProgress.phase === "reading"
                        ? "Reading zip…"
                        : uploadProgress.phase === "parsing"
                        ? `Reading pack (${uploadProgress.percent}%)…`
                        : "Sending manifest…"
                      : "Uploading…"
                    : "Upload Repack (.zip)"
                }
                onClick={() => document.getElementById("mt-upload-input").click()}
                disabled={!!busy}
                testid="mod-tracker-upload"
                primary
                title="Parses the zip locally in your browser and only sends a tiny manifest — works with 2 GB+ packs."
              />
              <input
                id="mt-upload-input"
                type="file"
                accept=".zip,application/zip"
                className="hidden"
                data-testid="mod-tracker-upload-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  handleUpload(f);
                  e.target.value = "";
                }}
              />
              <ToolbarButton
                icon={RefreshCw}
                label={busy === "refresh" ? "Checking…" : "Check for Updates"}
                onClick={handleRefresh}
                disabled={!!busy || sortedMods.length === 0}
                testid="mod-tracker-refresh"
              />
              <ToolbarButton
                icon={Wand2}
                label={busy === "autofill" ? "Matching…" : "Auto-fill Workshop IDs"}
                onClick={handleAutofill}
                disabled={!!busy || sortedMods.length === 0}
                testid="mod-tracker-autofill"
                title="Uses your Workshop collection if the URL is one, otherwise searches Steam Workshop by mod name."
              />
              {activePack?.undo_available && (
                <ToolbarButton
                  icon={Undo2}
                  label="Undo last upload"
                  onClick={undoUpload}
                  disabled={!!busy}
                  testid="mod-tracker-undo-upload"
                  title={
                    activePack.undo_snapshotted_at
                      ? `Roll ${activePack.name} back to its state before the ${new Date(activePack.undo_snapshotted_at).toLocaleString()} upload (${activePack.undo_mod_count || 0} mods). Single-shot.`
                      : "Roll this pack back to its state before the last upload."
                  }
                />
              )}
            </div>
          )}
        </div>

        {/* Persistent error banner — survives longer than a toast so it can't
            be missed if the MusicPlayer covers the corner. */}
        {opError && (
          <div
            className="mt-6 border border-[#C0392B] bg-[#3B1414]/60 px-4 py-3 flex items-start gap-3"
            data-testid="mod-tracker-op-error"
          >
            <ShieldAlert className="text-[#E27B7B] shrink-0 mt-0.5" size={16} />
            <div className="flex-1 min-w-0">
              <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#E27B7B]">
                // Operation failed
              </div>
              <p className="mt-1 text-[#F5A5A5] font-mono-ui text-xs break-words">{opError}</p>
              <p className="mt-1 text-[#8D9A87] font-mono-ui text-[10px]">
                Full details logged to browser DevTools → Console.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpError(null)}
              data-testid="mod-tracker-op-error-dismiss"
              className="text-[#8D9A87] hover:text-[#E27B7B] font-mono-ui text-xs px-2"
            >
              ✕
            </button>
          </div>
        )}

        {/* Pack tabs — split mods across as many packs as you want (e.g.
            Guns, Clothing, Maps). Every tab has its own upload / autofill /
            refresh flow, but the Mods= and Map= copy bars below concatenate
            every enabled pack sequentially. */}
        <PackTabsBar
          packs={packs || []}
          selectedPackId={activePack?.id}
          onSelect={setSelectedPackId}
          onCreate={createPack}
          onRename={renamePack}
          onDelete={deletePack}
          onReorder={reorderPacks}
          onToggleDev={toggleDevPack}
          canEdit={canEdit}
        />

        {/* Cross-pack duplicate warning. The Mods= copy string de-dupes
            silently (first occurrence wins), but admins need visibility so
            they can pick which pack owns the mod and prune the copies.
            Dev packs (flagged via the flask icon on the tab) are excluded
            from this scan so an intentionally overlapping Testers pack
            doesn't cry wolf. */}
        {duplicates.length > 0 && (
          <div
            className="mt-4 border border-[#C0A875]/60 bg-[#2A2313]/60 px-4 py-3 flex items-start gap-3"
            data-testid="mod-tracker-duplicate-warning"
          >
            <AlertTriangle className="text-[#C0A875] shrink-0 mt-0.5" size={16} />
            <div className="flex-1 min-w-0">
              <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#C0A875]">
                // {duplicates.length} mod{duplicates.length === 1 ? "" : "s"} appear in more than one production pack
              </div>
              <p className="mt-1 text-[#D9C494] text-xs">
                The <code className="font-mono-ui text-[#C0A875]">Mods=</code> line keeps only the <em>first</em> occurrence (Pack 1 → Pack N order). Remove the mod from the packs where you don't want it so status and enabled flags stay consistent.
                {(packs || []).some((p) => p.exclude_from_duplicates) && (
                  <span className="text-[#8D9A87]">
                    {" "}Dev packs ({(packs || []).filter((p) => p.exclude_from_duplicates).map((p) => p.name).join(", ")}) are excluded from this scan.
                  </span>
                )}
              </p>
              <ul className="mt-2 space-y-1 font-mono-ui text-[11px]">
                {duplicates.slice(0, 12).map((d) => (
                  <li key={d.id} data-testid={`mod-tracker-duplicate-${d.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        // Jump the user to the pack that owns the "kept"
                        // occurrence so they can review it first.
                        setSelectedPackId(d.occurrences[0].packId);
                      }}
                      className="text-[#C0A875] hover:text-[#E2E4DF] underline underline-offset-2"
                      title={`Open ${d.occurrences[0].packName}`}
                    >
                      {d.id}
                    </button>
                    <span className="text-[#8D9A87]">
                      {" "}→ in {d.occurrences.map((o) => `${o.packName}${o.enabled ? "" : " (off)"}`).join(", ")}
                    </span>
                  </li>
                ))}
                {duplicates.length > 12 && (
                  <li className="text-[#5C6B51]">…and {duplicates.length - 12} more</li>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Repack URL + load-order string */}
        <div className="mt-8 grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card>
            <CardHeader
              title={`Repack Steam Workshop URL — ${activePack?.name || "current pack"}`}
              subtitle="Used by the Auto-fill button — paste your collection URL for the best results. Each pack has its own URL."
            />
            {/* Pack description — appears above the URL and doubles as the
                tooltip you see when hovering the pack tab. Optional. */}
            {canEdit ? (
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                <input
                  type="text"
                  value={descDraft}
                  onChange={(e) => {
                    setDescDraft(e.target.value);
                    setDescDirty(e.target.value !== (description || ""));
                  }}
                  maxLength={500}
                  placeholder="Short description — e.g. Guns, vests & ammo (shown as tooltip on the pack tab)"
                  data-testid="mod-tracker-pack-description"
                  className="flex-1 bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none placeholder:italic placeholder:text-[#5C6B51]"
                />
                <button
                  type="button"
                  onClick={saveDescription}
                  disabled={!descDirty || busy === "settings"}
                  data-testid="mod-tracker-pack-description-save"
                  className="inline-flex items-center gap-1.5 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors"
                >
                  <Save size={12} /> Save
                </button>
              </div>
            ) : (
              description && (
                <p
                  className="mt-3 text-[#8D9A87] font-mono-ui text-xs italic border-l-2 border-[#283524] pl-3"
                  data-testid="mod-tracker-pack-description-view"
                >
                  {description}
                </p>
              )
            )}
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <input
                type="url"
                value={urlDraft}
                onChange={(e) => {
                  setUrlDraft(e.target.value);
                  setUrlDirty(e.target.value !== (workshop_url || ""));
                }}
                readOnly={!canEdit}
                placeholder="https://steamcommunity.com/sharedfiles/filedetails/?id=…"
                data-testid="mod-tracker-workshop-url"
                className="flex-1 bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
              />
              {workshop_url && (
                <a
                  href={workshop_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="mod-tracker-workshop-open"
                  className="inline-flex items-center justify-center gap-1.5 border border-[#283524] hover:border-[#C0A875] text-[#8D9A87] hover:text-[#C0A875] px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] transition-colors"
                >
                  <ExternalLink size={12} /> Open
                </a>
              )}
              {canEdit && (
                <button
                  type="button"
                  onClick={saveUrl}
                  disabled={!urlDirty || busy === "settings"}
                  data-testid="mod-tracker-workshop-save"
                  className="inline-flex items-center gap-1.5 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors"
                >
                  <Save size={12} /> Save
                </button>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader
              title={`Production Mods= (${enabledCount} enabled across ${prodPacks.length} pack${prodPacks.length === 1 ? "" : "s"})`}
              subtitle="Every production pack's enabled mods concatenated in Pack 1 → Pack N order. Dev packs skipped. Duplicates removed."
            />
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <code
                data-testid="mod-tracker-mods-string"
                className="flex-1 bg-[#0D110C] border border-[#283524] text-[#C0A875] px-3 py-2 font-mono-ui text-[11px] break-all overflow-x-auto"
              >
                {loadOrderString === "Mods=" ? <span className="text-[#5C6B51]">Mods=</span> : loadOrderString}
              </code>
              <button
                type="button"
                onClick={() => copyText(loadOrderString, "Production load-order string")}
                disabled={enabledCount === 0}
                data-testid="mod-tracker-copy-mods"
                className="inline-flex items-center gap-1.5 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
          </Card>
        </div>

        {/* Map= copy bar + Base maps editor. Every enabled mod's map folder
            names are appended in load order, then the admin-typed base maps
            (Muldraugh, KY etc.) come last so the whole line is paste-ready. */}
        <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card>
            <CardHeader
              title={`Production Map= (${mapCount} maps across ${prodPacks.length} pack${prodPacks.length === 1 ? "" : "s"})`}
              subtitle="Auto-built from every enabled production mod's media/maps/ folders, plus your base maps below."
            />
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <code
                data-testid="mod-tracker-map-string"
                className="flex-1 bg-[#0D110C] border border-[#283524] text-[#C0A875] px-3 py-2 font-mono-ui text-[11px] break-all overflow-x-auto"
              >
                {mapString === "Map=" ? <span className="text-[#5C6B51]">Map=</span> : mapString}
              </code>
              <button
                type="button"
                onClick={() => copyText(mapString, "Production Map= line")}
                disabled={mapCount === 0}
                data-testid="mod-tracker-copy-map"
                className="inline-flex items-center gap-1.5 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Base maps (not in any mod)"
              subtitle="Semicolon- or line-separated. Base-game maps like Muldraugh, KY / West Point, KY that Zomboid ships with."
            />
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <textarea
                value={baseMapsDraft}
                onChange={(e) => {
                  setBaseMapsDraft(e.target.value);
                  setBaseMapsDirty(e.target.value !== (base_maps || ""));
                }}
                readOnly={!canEdit}
                placeholder="Muldraugh, KY; West Point, KY; Rosewood, KY; Riverside, KY"
                rows={2}
                data-testid="mod-tracker-base-maps"
                className="flex-1 bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y min-h-[42px]"
              />
              {canEdit && (
                <button
                  type="button"
                  onClick={saveBaseMaps}
                  disabled={!baseMapsDirty || busy === "settings"}
                  data-testid="mod-tracker-base-maps-save"
                  className="inline-flex items-center gap-1.5 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors self-start"
                >
                  <Save size={12} /> Save
                </button>
              )}
            </div>
          </Card>
        </div>

        {/* Dev / testers server load order. Rendered only when at least
            one pack is flagged as dev — keeps the primary loader clean for
            the vast majority of setups that don't use a dev server. */}
        {devPacks.length > 0 && (
          <div
            className="mt-6 border border-[#5D8FBF]/40 bg-[#0F1A24]/40 p-4 space-y-4"
            data-testid="mod-tracker-dev-loader"
          >
            <button
              type="button"
              onClick={() => setDevLoaderOpen((v) => !v)}
              data-testid="mod-tracker-dev-loader-toggle"
              className="w-full flex items-center gap-2 text-left group"
              title={devLoaderOpen ? "Hide dev / testers load order" : "Show dev / testers load order"}
            >
              {devLoaderOpen
                ? <ChevronDown size={14} className="text-[#8DB4D9]" />
                : <ChevronRight size={14} className="text-[#8DB4D9]" />}
              <FlaskConical className="text-[#8DB4D9]" size={14} />
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-[#8DB4D9] group-hover:text-[#B8D4EE]">
                // Dev / testers server load order — {devPacks.map((p) => p.name).join(", ")}
              </span>
              {!devLoaderOpen && (
                <span className="ml-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#6B8299]">
                  {devEnabledCount} mods · {devMapCount} maps
                </span>
              )}
            </button>
            {devLoaderOpen && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Card>
                  <CardHeader
                    title={`Dev Mods= (${devEnabledCount} enabled across ${devPacks.length} pack${devPacks.length === 1 ? "" : "s"})`}
                    subtitle="Just the dev / testers packs. Paste this into your dev server config."
                  />
                  <div className="flex flex-col sm:flex-row gap-2 mt-3">
                    <code
                      data-testid="mod-tracker-dev-mods-string"
                      className="flex-1 bg-[#0D110C] border border-[#2A3B4A] text-[#8DB4D9] px-3 py-2 font-mono-ui text-[11px] break-all overflow-x-auto"
                    >
                      {devLoadOrderString === "Mods=" ? <span className="text-[#5C6B51]">Mods=</span> : devLoadOrderString}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyText(devLoadOrderString, "Dev load-order string")}
                      disabled={devEnabledCount === 0}
                      data-testid="mod-tracker-copy-dev-mods"
                      className="inline-flex items-center gap-1.5 border border-[#5D8FBF] text-[#8DB4D9] hover:bg-[#5D8FBF]/15 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors"
                    >
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                </Card>
                <Card>
                  <CardHeader
                    title={`Dev Map= (${devMapCount} maps across ${devPacks.length} pack${devPacks.length === 1 ? "" : "s"})`}
                    subtitle="Auto-built from every enabled dev mod's media/maps/ folders. Base maps are NOT auto-appended here — add them to the dev server config manually if needed."
                  />
                  <div className="flex flex-col sm:flex-row gap-2 mt-3">
                    <code
                      data-testid="mod-tracker-dev-map-string"
                      className="flex-1 bg-[#0D110C] border border-[#2A3B4A] text-[#8DB4D9] px-3 py-2 font-mono-ui text-[11px] break-all overflow-x-auto"
                    >
                      {devMapString === "Map=" ? <span className="text-[#5C6B51]">Map=</span> : devMapString}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyText(devMapString, "Dev Map= line")}
                      disabled={devMapCount === 0}
                      data-testid="mod-tracker-copy-dev-map"
                      className="inline-flex items-center gap-1.5 border border-[#5D8FBF] text-[#8DB4D9] hover:bg-[#5D8FBF]/15 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors"
                    >
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        <ManualBackup
          canEdit={canEdit}
          production={{ mods: loadOrderString, workshop: workshopString, map: mapString }}
          copyText={copyText}
        />

        {/* Bulk-apply toolbar for the Update Check column. Only shown when
             the admin has selected at least one row. Sits above the table
             so it never overlaps the row itself. */}
        {canEditStatus && selectedCount > 0 && (
          <div
            data-testid="mod-tracker-bulk-toolbar"
            className="mt-10 -mb-4 flex flex-wrap items-center gap-3 bg-[#151B14] border border-[#C0A875]/50 px-4 py-3"
          >
            <span className="font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#C0A875]">
              {selectedCount} selected
            </span>
            <span className="text-[#5C6B51]">/</span>
            <label className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#8D9A87]">
              Apply Update Check →
            </label>
            <select
              data-testid="mod-tracker-bulk-update-check"
              disabled={bulkBusy}
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value;
                // Reset the select back to placeholder immediately so the
                // admin can pick the same value again next time.
                e.target.value = "__pick__";
                if (v !== "__pick__") bulkApplyUpdateCheck(v);
              }}
              className="bg-[#0D110C] px-3 py-1.5 border border-[#5C6B51] hover:border-[#C0A875] text-[#E2E4DF] font-mono-ui text-[10px] uppercase tracking-[0.2em] focus:outline-none focus:border-[#C0A875] disabled:opacity-50"
            >
              <option value="__pick__">Pick a status…</option>
              {(updateCheckStates || ["", "WAIT", "READY", "UPDATED"]).map((s) => (
                <option key={s || "blank"} value={s}>
                  {s === "" ? "— (clear)" : UPDATE_CHECK_LABELS[s] || s}
                </option>
              ))}
            </select>
            {bulkBusy && (
              <span className="inline-flex items-center gap-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#8D9A87]">
                <Loader2 size={12} className="animate-spin" /> Applying…
              </span>
            )}
            <button
              type="button"
              onClick={clearSelection}
              disabled={bulkBusy}
              data-testid="mod-tracker-bulk-clear"
              className="ml-auto font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#8D9A87] hover:text-[#E2E4DF] disabled:opacity-50"
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Table */}
        <div className="mt-10 border border-[#283524] overflow-hidden">
          <div className="overflow-x-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => {
                const { active, over } = e;
                if (!over || active.id === over.id) return;
                const from = sortedMods.findIndex((m) => m.mod_id === active.id);
                const to = sortedMods.findIndex((m) => m.mod_id === over.id);
                if (from >= 0 && to >= 0) reorderMods(from, to);
              }}
            >
              <SortableContext
                items={sortedMods.map((m) => m.mod_id)}
                strategy={verticalListSortingStrategy}
              >
                <table className="w-full text-sm" data-testid="mod-tracker-table">
                  <thead>
                    <tr className="bg-[#151B14] border-b border-[#283524] text-left">
                      {canEdit && <Th className="w-8" title="Drag to reorder"></Th>}
                      <Th className="w-12 text-center" title="Include in Mods= line?">On</Th>
                      <Th className="w-16 text-center">#</Th>
                      <Th>Mod Name</Th>
                      <Th>Mod ID</Th>
                      <Th className="w-52">Workshop</Th>
                      <Th className="w-40">Steam Updated</Th>
                      <Th className="w-40">Last Repacked</Th>
                      <Th className="w-52">Status</Th>
                      <Th className="w-48" title="Manual triage flag for update rollout">
                        {canEditStatus && selectableIds.length > 0 ? (
                          <span className="inline-flex items-center gap-2">
                            <BulkSelectAllCheckbox
                              allSelected={allSelected}
                              someSelected={someSelected}
                              onToggle={toggleSelectAll}
                              count={selectableIds.length}
                            />
                            Update Check
                          </span>
                        ) : (
                          "Update Check"
                        )}
                      </Th>
                      {canEdit && <Th className="w-14"></Th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMods.length === 0 ? (
                      <tr>
                        <td colSpan={canEdit ? 11 : 9} className="px-4 py-16 text-center">
                          <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-[#5C6B51]">
                            // no mods tracked yet
                          </p>
                          {canEdit && (
                            <p className="mt-2 text-[#8D9A87] text-xs">Upload your repack .zip to populate this table.</p>
                          )}
                        </td>
                      </tr>
                    ) : (
                      sortedMods.map((m) => (
                        <ModRow
                          key={m.mod_id}
                          mod={m}
                          canEdit={canEdit}
                          canEditStatus={canEditStatus}
                          statuses={statuses}
                          updateCheckStates={updateCheckStates}
                          onPatch={(body) => patchMod(m.mod_id, body)}
                          onDelete={() => deleteMod(m.mod_id)}
                          selected={selectedIds.has(m.mod_id)}
                          onToggleSelected={() => toggleSelected(m.mod_id)}
                          duplicateInPacks={
                            duplicateModIds.has((m.mod_id || "").toLowerCase())
                              ? (duplicates.find((d) => d.id.toLowerCase() === (m.mod_id || "").toLowerCase())?.occurrences || [])
                              : null
                          }
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* Footer hint */}
        <p className="mt-6 text-[#5C6B51] text-xs font-mono-ui">
          {canEdit
            ? "Tip — a row is CURRENT when your local pack is newer than Steam's last-updated date, OR when local + Steam byte totals match exactly. NEEDS UPDATE only fires on stale mtime (byte mismatches alone are ignored — Steam packages mods differently). Hover Last Repacked for the full breakdown (sizes, version, mtimes). Drag ⋮⋮ to reorder rows."
            : "You have read-only access plus Status editing. Ask a super-admin for full edit rights if you need it."}
        </p>
      </div>
    </section>
  );
}

// ------- Row -------

function ModRow({ mod, canEdit, canEditStatus, statuses, updateCheckStates, onPatch, onDelete, duplicateInPacks, selected, onToggleSelected }) {
  const rowStatus = rowStatusFor(mod);
  const isEnabled = mod.enabled !== false;

  // Sortable — only draggable when the caller can edit. Non-editors still
  // render the row; they just don't get a working drag handle.
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mod.mod_id, disabled: !canEdit });
  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Lift the dragged row visually.
    opacity: isDragging ? 0.8 : 1,
    boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.5)" : undefined,
    position: "relative",
    zIndex: isDragging ? 20 : "auto",
  };

  // Row colouring — the primary read-out. Disabled mods dim the whole row so
  // the eye skips them, even if they're technically "current".
  const baseBg =
    rowStatus === "needs-update"
      ? "bg-[#3B1414] hover:bg-[#4A1919]"
      : rowStatus === "current"
      ? "bg-[#132917] hover:bg-[#17331C]"
      : "bg-[#1A1E19] hover:bg-[#20261F]";
  const rowBg = isEnabled ? baseBg : "bg-[#111511] hover:bg-[#151B14] opacity-60";

  return (
    <tr
      ref={setNodeRef}
      style={dragStyle}
      className={`border-b border-[#283524] transition-colors ${rowBg}`}
      data-testid={`mod-row-${mod.mod_id}`}
    >
      {canEdit && (
        <Td className="text-center">
          <button
            type="button"
            {...attributes}
            {...listeners}
            title="Drag to reorder"
            data-testid={`mod-row-drag-${mod.mod_id}`}
            className="w-6 h-6 inline-flex items-center justify-center text-[#5C6B51] hover:text-[#C0A875] cursor-grab active:cursor-grabbing touch-none"
          >
            <GripVertical size={14} />
          </button>
        </Td>
      )}
      <Td className="text-center">
        <EnabledCheckbox
          value={isEnabled}
          onChange={(v) => onPatch({ enabled: v })}
          disabled={!canEdit}
          modId={mod.mod_id}
        />
      </Td>
      <Td className="text-center text-[#8D9A87] font-mono-ui">
        <InlineNumber
          value={mod.load_order}
          onSave={(n) => onPatch({ load_order: n })}
          disabled={!canEdit}
          testid={`mod-row-load-order-${mod.mod_id}`}
        />
      </Td>
      <Td className={isEnabled ? "text-[#E2E4DF]" : "text-[#8D9A87] line-through"}>
        <span className="inline-flex items-center gap-1.5 flex-wrap">
          <span>{mod.mod_name || mod.mod_id}</span>
          {mod.mod_name_manual && (
            <span
              title="Manually renamed. Re-uploading won't overwrite this name."
              data-testid={`mod-row-name-manual-${mod.mod_id}`}
              className="text-[9px] font-mono-ui uppercase tracking-[0.2em] text-[#C0A875]/70 border border-[#C0A875]/30 px-1"
            >
              edited
            </span>
          )}
          {duplicateInPacks && duplicateInPacks.length > 1 && (
            // Also in other packs. Silently de-duped in the Mods= string
            // but the admin needs to know so status/enabled flags stay
            // consistent across packs.
            <span
              title={`Also in: ${duplicateInPacks.map((o) => `${o.packName}${o.enabled ? "" : " (off)"}`).join(", ")}`}
              data-testid={`mod-row-duplicate-${mod.mod_id}`}
              className="text-[9px] font-mono-ui uppercase tracking-[0.2em] text-[#C0A875]/80 border border-[#C0A875]/50 bg-[#C0A875]/10 px-1"
            >
              in {duplicateInPacks.length} packs
            </span>
          )}
        </span>
        {mod.workshop_confidence && CONFIDENCE_STYLES[mod.workshop_confidence] && (
          <span
            title={`Auto-matched (${CONFIDENCE_STYLES[mod.workshop_confidence].label}). Verify the Workshop link before trusting.`}
            data-testid={`mod-row-confidence-${mod.mod_id}`}
            className="ml-2 inline-block align-middle w-2 h-2 rounded-full"
            style={{ background: CONFIDENCE_STYLES[mod.workshop_confidence].color }}
          />
        )}
        {mod.maps && mod.maps.length > 0 && (
          // Small caption showing every map folder this mod ships. These are
          // what land in the Map= copy line (when the mod is enabled).
          <div
            data-testid={`mod-row-maps-${mod.mod_id}`}
            className="mt-1 font-mono-ui text-[10px] text-[#8D9A87] flex flex-wrap gap-x-1.5"
            title="Map folders this mod contributes"
          >
            <span className="text-[#5C6B51]">maps:</span>
            {mod.maps.map((mp) => (
              <span key={mp} className="text-[#C0A875]/80">{mp}</span>
            ))}
          </div>
        )}
      </Td>
      <Td className="text-[#8D9A87] font-mono-ui text-xs">
        <InlineText
          value={mod.mod_id}
          placeholder="—"
          onSave={(v) => onPatch({ mod_id: v })}
          disabled={!canEdit}
          renderView={(v) => (
            <span className="inline-flex items-center gap-1.5">
              <span>{v}</span>
              {mod.mod_id_manual && (
                <span
                  title="Manually edited. Re-uploading won't overwrite this Mod ID (matched by folder). Clear the field and Enter to revert."
                  data-testid={`mod-row-id-manual-${mod.mod_id}`}
                  className="text-[9px] font-mono-ui uppercase tracking-[0.2em] text-[#C0A875]/70 border border-[#C0A875]/30 px-1"
                >
                  edited
                </span>
              )}
            </span>
          )}
          testid={`mod-row-id-${mod.mod_id}`}
        />
      </Td>
      <Td>
        <InlineText
          value={mod.workshop_id || ""}
          placeholder="—"
          onSave={(v) => onPatch({ workshop_id: v })}
          disabled={!canEdit}
          renderView={(v) =>
            v ? (
              <a
                href={`https://steamcommunity.com/sharedfiles/filedetails/?id=${v}`}
                target="_blank"
                rel="noreferrer"
                className="text-[#7DB0E8] hover:text-[#C0A875] underline underline-offset-2 font-mono-ui text-xs"
                onClick={(e) => e.stopPropagation()}
                data-testid={`mod-row-workshop-link-${mod.mod_id}`}
              >
                {v}
              </a>
            ) : (
              <span className="text-[#5C6B51] font-mono-ui text-xs">—</span>
            )
          }
          testid={`mod-row-workshop-${mod.mod_id}`}
        />
      </Td>
      <Td className="text-[#A8B5A0] font-mono-ui text-xs">{formatDate(mod.steam_last_updated)}</Td>
      <Td className="text-[#A8B5A0] font-mono-ui text-xs">
        <InlineDate
          value={mod.last_repacked}
          onSave={(iso) => onPatch({ last_repacked: iso })}
          disabled={!canEdit}
          testid={`mod-row-last-repacked-${mod.mod_id}`}
          manual={!!mod.last_repacked_manual}
          modId={mod.mod_id}
          modInfoMtime={mod.mod_info_mtime}
          folderMaxMtime={mod.folder_max_mtime}
          modVersion={mod.mod_version}
          localSize={mod.total_size}
          steamSize={mod.steam_file_size}
        />
      </Td>
      <Td>
        <StatusChip
          value={mod.status}
          statuses={statuses}
          disabled={!canEditStatus}
          onChange={(v) => onPatch({ status: v })}
          rowStatus={rowStatus}
          modId={mod.mod_id}
        />
      </Td>
      <Td>
        <div className="flex items-center gap-2">
          {canEditStatus && (
            <BulkSelectRowCheckbox
              checked={!!selected}
              onToggle={onToggleSelected}
              modId={mod.mod_id}
            />
          )}
          <UpdateCheckChip
            value={mod.update_check}
            states={updateCheckStates}
            disabled={!canEditStatus}
            onChange={(v) => onPatch({ update_check: v })}
            rowStatus={rowStatus}
            modId={mod.mod_id}
          />
        </div>
      </Td>
      {canEdit && (
        <Td className="text-center">
          <button
            type="button"
            onClick={onDelete}
            title="Remove from tracker"
            data-testid={`mod-row-delete-${mod.mod_id}`}
            className="w-7 h-7 border border-[#5C1A1A] text-[#C0392B] hover:bg-[#C0392B]/15 inline-flex items-center justify-center transition-colors"
          >
            <Trash2 size={12} />
          </button>
        </Td>
      )}
    </tr>
  );
}

function EnabledCheckbox({ value, onChange, disabled, modId }) {
  // Chunky terminal-style checkbox so it reads well against the row colour
  // and is a clear "included?" affordance. `enabled` was added post-launch,
  // so we treat `undefined` as ON to avoid retroactively hiding mods.
  const on = value !== false;
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!on)}
      disabled={disabled}
      title={disabled ? "" : on ? "Included in Mods= line — click to exclude" : "Excluded from Mods= line — click to include"}
      data-testid={`mod-row-enabled-${modId}`}
      className={`w-5 h-5 border-2 flex items-center justify-center transition-colors ${
        on ? "bg-[#C0A875] border-[#C0A875] text-[#0D110C]" : "bg-transparent border-[#5C6B51] hover:border-[#E2E4DF] text-transparent"
      } ${disabled ? "cursor-default opacity-70" : "cursor-pointer"}`}
    >
      <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2 6 L5 9 L10 3" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="square" />
      </svg>
    </button>
  );
}

function StatusChip({ value, statuses, disabled, onChange, rowStatus, modId }) {
  const cur = (value || "NOT_TESTED").toUpperCase();
  const style = STATUS_STYLES[cur] || STATUS_STYLES.NOT_TESTED;
  // Also surface the row-status flag inline (needs update / current) since
  // this column doubles as the operational read-out.
  const flagText =
    rowStatus === "needs-update" ? "NEEDS UPDATE" : rowStatus === "current" ? "CURRENT" : "NO STEAM DATE";
  const flagColor =
    rowStatus === "needs-update" ? "text-[#F5A5A5]" : rowStatus === "current" ? "text-[#9BE68F]" : "text-[#8D9A87]";
  if (disabled) {
    return (
      <div className="flex flex-col gap-1">
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 border font-mono-ui text-[10px] uppercase tracking-[0.2em] w-fit ${style}`}>
          {cur.replace("_", " ")}
        </span>
        <span className={`font-mono-ui text-[9px] uppercase tracking-[0.2em] ${flagColor}`}>{flagText}</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <select
        value={cur}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`mod-row-status-${modId}`}
        className={`bg-[#0D110C] px-2 py-1 border font-mono-ui text-[10px] uppercase tracking-[0.2em] w-fit focus:outline-none focus:border-[#C0A875] ${style}`}
      >
        {(statuses || []).map((s) => (
          <option key={s} value={s}>
            {s.replace("_", " ")}
          </option>
        ))}
      </select>
      <span className={`font-mono-ui text-[9px] uppercase tracking-[0.2em] ${flagColor}`}>{flagText}</span>
    </div>
  );
}

// Update Check pillbox — separate axis from Status. Blank + row auto-
// flagged as needing an update = red styling so admins can see at a
// glance which rows haven't been triaged after a Steam refresh.
const UPDATE_CHECK_STYLES = {
  "": "border-[#5C6B51] text-[#8D9A87] bg-[#151B14]",
  WAIT: "border-[#C0A875] text-[#C0A875] bg-[#2A2313]",
  READY: "border-[#5D8FBF] text-[#8DB4D9] bg-[#0F1A24]",
  UPDATED: "border-[#4A7A3E] text-[#9BE68F] bg-[#152318]",
};
const UPDATE_CHECK_LABELS = {
  "": "—",
  WAIT: "WAIT FOR UPDATE",
  READY: "READY TO UPDATE",
  UPDATED: "UPDATED",
};

function UpdateCheckChip({ value, states, disabled, onChange, rowStatus, modId }) {
  const cur = (value || "").toUpperCase();
  const isRedBlank = !cur && rowStatus === "needs-update";
  // When the auto-status flags "needs-update" AND the admin hasn't
  // triaged it (blank), paint the whole cell red so it screams.
  const style = isRedBlank
    ? "border-[#C0392B] text-[#E27B7B] bg-[#3B1414]"
    : UPDATE_CHECK_STYLES[cur] || UPDATE_CHECK_STYLES[""];
  const label = UPDATE_CHECK_LABELS[cur] || UPDATE_CHECK_LABELS[""];
  if (disabled) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 border font-mono-ui text-[10px] uppercase tracking-[0.2em] w-fit ${style}`}
        data-testid={`mod-row-update-check-${modId}`}
      >
        {isRedBlank ? "" : label}
      </span>
    );
  }
  return (
    <select
      value={cur}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`mod-row-update-check-${modId}`}
      className={`bg-[#0D110C] px-2 py-1 border font-mono-ui text-[10px] uppercase tracking-[0.2em] w-fit focus:outline-none focus:border-[#C0A875] ${style}`}
    >
      {(states || ["", "WAIT", "READY", "UPDATED"]).map((s) => (
        <option key={s || "blank"} value={s}>
          {s === "" ? "—" : UPDATE_CHECK_LABELS[s] || s}
        </option>
      ))}
    </select>
  );
}

// Small terminal-style checkbox used both per-row (Update Check column) and
// in the header for select-all. Rendered as a native <input type=checkbox>
// under the hood so keyboard + a11y just work, but styled to match the
// existing pill / EnabledCheckbox aesthetic.
function BulkSelectRowCheckbox({ checked, onToggle, modId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={checked ? "Deselect" : "Select for bulk apply"}
      data-testid={`mod-row-select-${modId}`}
      className={`w-4 h-4 shrink-0 border flex items-center justify-center transition-colors ${
        checked
          ? "bg-[#C0A875] border-[#C0A875] text-[#0D110C]"
          : "bg-transparent border-[#5C6B51] hover:border-[#C0A875] text-transparent"
      }`}
      aria-pressed={checked}
    >
      <svg width="8" height="8" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2 6 L5 9 L10 3" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="square" />
      </svg>
    </button>
  );
}

function BulkSelectAllCheckbox({ allSelected, someSelected, onToggle, count }) {
  const state = allSelected ? "all" : someSelected ? "some" : "none";
  return (
    <button
      type="button"
      onClick={onToggle}
      title={
        state === "all"
          ? `All ${count} selected — click to clear`
          : state === "some"
          ? "Some selected — click to clear"
          : `Select all ${count} mods in this pack`
      }
      data-testid="mod-tracker-select-all"
      aria-pressed={state !== "none"}
      className={`w-4 h-4 shrink-0 border flex items-center justify-center transition-colors ${
        state === "none"
          ? "bg-transparent border-[#5C6B51] hover:border-[#C0A875] text-transparent"
          : "bg-[#C0A875] border-[#C0A875] text-[#0D110C]"
      }`}
    >
      {state === "all" && (
        <svg width="8" height="8" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2 6 L5 9 L10 3" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="square" />
        </svg>
      )}
      {state === "some" && (
        // Minus glyph for indeterminate state — mirrors Gmail / Notion.
        <svg width="8" height="8" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2 6 L10 6" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
        </svg>
      )}
    </button>
  );
}

// ------- Inline editors -------

function InlineText({ value, placeholder, onSave, disabled, renderView, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  if (!editing) {
    return (
      <div className="flex items-center gap-2 group">
        <div className="flex-1 min-w-0">{renderView ? renderView(value) : value || placeholder}</div>
        {!disabled && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            title="Edit"
            data-testid={testid ? `${testid}-edit` : undefined}
            className="opacity-0 group-hover:opacity-100 text-[#5C6B51] hover:text-[#C0A875] transition-opacity"
          >
            <Pencil size={11} />
          </button>
        )}
      </div>
    );
  }

  const commit = () => {
    onSave(draft.trim());
    setEditing(false);
  };
  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      autoFocus
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Escape") { e.preventDefault(); setDraft(value || ""); setEditing(false); }
      }}
      placeholder={placeholder}
      data-testid={testid ? `${testid}-input` : undefined}
      className="w-full bg-[#0D110C] border border-[#C0A875] text-[#E2E4DF] px-2 py-1 font-mono-ui text-xs focus:outline-none"
    />
  );
}

function InlineNumber({ value, onSave, disabled, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? 1));
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(String(value ?? 1));
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => !disabled && setEditing(true)}
        title={disabled ? "" : "Edit load order"}
        data-testid={testid}
        className={`px-2 py-1 font-mono-ui text-xs ${disabled ? "cursor-default text-[#8D9A87]" : "text-[#E2E4DF] hover:text-[#C0A875]"}`}
      >
        {value ?? "—"}
      </button>
    );
  }
  const commit = () => {
    const n = parseInt(draft, 10);
    if (!Number.isNaN(n) && n > 0) onSave(n);
    setEditing(false);
  };
  return (
    <input
      ref={inputRef}
      type="number"
      min={1}
      value={draft}
      autoFocus
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
      }}
      data-testid={testid ? `${testid}-input` : undefined}
      className="w-16 bg-[#0D110C] border border-[#C0A875] text-[#E2E4DF] px-2 py-1 font-mono-ui text-xs text-center focus:outline-none"
    />
  );
}

function InlineDate({ value, onSave, disabled, testid, manual, modId, modInfoMtime, folderMaxMtime, modVersion, localSize, steamSize }) {
  // Click to reveal a native date input pre-populated with the current
  // Last Repacked date. Also exposes a one-click "Today" button so the
  // common "I just re-packed this one mod" case is a single tap.
  const [editing, setEditing] = useState(false);
  const asDateInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    // Local yyyy-mm-dd so the date-picker matches what the user's clock
    // shows, not UTC.
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const [draft, setDraft] = useState(asDateInput(value));
  useEffect(() => { setDraft(asDateInput(value)); }, [value]);

  const commit = (v) => {
    if (!v) { setEditing(false); return; }
    onSave(v);
    setEditing(false);
  };

  if (!editing) {
    // Build a diagnostic tooltip so admins can see WHY a row is (or isn't)
    // flagged as needing an update — plain "9/6/2026" hides time-of-day
    // and doesn't say whether mod.info vs textures drove the decision.
    const localFmt = (iso) => {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString();
    };
    const tooltipLines = [];
    // Byte-size comparison — positive-only. Match = definitely synced.
    // Mismatch is ambiguous (Steam packages mods in its own format, so
    // byte totals rarely match exactly even when up to date) and falls
    // back to the mtime check.
    if (typeof localSize === "number" && typeof steamSize === "number" && localSize > 0 && steamSize > 0) {
      const match = localSize === steamSize;
      tooltipLines.push(
        `Local size: ${formatBytes(localSize) || localSize} · Steam: ${formatBytes(steamSize) || steamSize}` +
        (match ? " (✓ byte-exact — definitely in sync)" : ` (Δ ${formatBytes(Math.abs(localSize - steamSize)) || Math.abs(localSize - steamSize)}, ambiguous — Steam packages differently)`)
      );
    } else if (typeof localSize === "number") {
      tooltipLines.push(`Local size: ${formatBytes(localSize) || localSize} · Steam size: unknown (needs Workshop ID + Refresh)`);
    }
    if (modVersion) tooltipLines.push(`mod.info version: ${modVersion}`);
    tooltipLines.push(`Effective Last Repacked: ${localFmt(value) || "—"}`);
    if (modInfoMtime) tooltipLines.push(`mod.info mtime: ${localFmt(modInfoMtime)}`);
    if (folderMaxMtime) tooltipLines.push(`Newest file in folder: ${localFmt(folderMaxMtime)}`);
    if (manual) tooltipLines.push("Manually set — locked from re-upload overwrites.");
    else if (modInfoMtime) tooltipLines.push("Auto — driven by mod.info file mtime.");
    return (
      <div className="flex items-center gap-2 group">
        <span title={tooltipLines.join("\n")}>{formatDate(value)}</span>
        {manual && (
          <span
            title="Manually set. Re-uploads won't overwrite this — clear to fall back to the mod folder's file mtime."
            data-testid={modId ? `mod-row-last-repacked-manual-${modId}` : undefined}
            className="text-[9px] font-mono-ui uppercase tracking-[0.2em] text-[#C0A875]/70 border border-[#C0A875]/30 px-1"
          >
            edited
          </span>
        )}
        {!disabled && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            title="Edit last-repacked date"
            data-testid={testid ? `${testid}-edit` : undefined}
            className="opacity-0 group-hover:opacity-100 text-[#5C6B51] hover:text-[#C0A875] transition-opacity"
          >
            <Pencil size={11} />
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <input
        type="date"
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(draft); }
          if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
        }}
        data-testid={testid ? `${testid}-input` : undefined}
        className="bg-[#0D110C] border border-[#C0A875] text-[#E2E4DF] px-2 py-1 font-mono-ui text-xs focus:outline-none"
      />
      <button
        type="button"
        // Mousedown fires before the input's blur so the click actually
        // registers instead of being cancelled by the blur → commit flow.
        onMouseDown={(e) => {
          e.preventDefault();
          const today = asDateInput(new Date().toISOString());
          setDraft(today);
          commit(today);
        }}
        title="Set to today"
        data-testid={testid ? `${testid}-today` : undefined}
        className="border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-2 py-1 font-mono-ui text-[10px] uppercase tracking-[0.2em]"
      >
        Today
      </button>
    </div>
  );
}

// ------- Small building blocks -------

function CenterCard({ children }) {
  return (
    <section className="py-24 border-t border-[#283524] min-h-[60vh]">
      <div className="max-w-md mx-auto px-6 text-center">{children}</div>
    </section>
  );
}

function Card({ children }) {
  return <div className="bg-[#151B14] border border-[#283524] p-4 sm:p-5">{children}</div>;
}

function CardHeader({ title, subtitle }) {
  return (
    <div>
      <div className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-[#8D9A87]">// {title}</div>
      {subtitle && <p className="mt-1 text-[#5C6B51] font-mono-ui text-[10px]">{subtitle}</p>}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] font-medium ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}

function LegendChip({ color, text, count, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-2 border border-[#283524] px-2 py-1 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#E2E4DF]"
    >
      <span className="w-2.5 h-2.5" style={{ background: color }} />
      {text}
      <span className="text-[#8D9A87]">×{count}</span>
    </span>
  );
}

function ToolbarButton({ icon: Icon, label, onClick, disabled, testid, primary, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={title}
      className={`inline-flex items-center gap-1.5 px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        primary
          ? "bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] font-semibold"
          : "border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15"
      }`}
    >
      <Icon size={12} />
      {label}
    </button>
  );
}

// Kept so eslint doesn't drop the BACKEND_URL import if we later need it.
export { BACKEND_URL };

// ------- Pack Tabs -------

function PackTabsBar({ packs, selectedPackId, onSelect, onCreate, onRename, onDelete, onReorder, onToggleDev, canEdit }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <div
      className="mt-8 flex flex-wrap items-center gap-2 border-b border-[#283524] pb-3"
      data-testid="mod-tracker-pack-tabs"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => {
          const { active, over } = e;
          if (!over || active.id === over.id) return;
          const from = packs.findIndex((p) => p.id === active.id);
          const to = packs.findIndex((p) => p.id === over.id);
          if (from >= 0 && to >= 0) onReorder(from, to);
        }}
      >
        <SortableContext items={packs.map((p) => p.id)} strategy={horizontalListSortingStrategy}>
          <div className="flex flex-wrap items-center gap-2">
            {packs.map((p) => (
              <PackTab
                key={p.id}
                pack={p}
                active={p.id === selectedPackId}
                canEdit={canEdit}
                canDelete={canEdit && packs.length > 1}
                onSelect={() => onSelect(p.id)}
                onRename={(name) => onRename(p.id, name)}
                onDelete={() => onDelete(p.id)}
                onToggleDev={() => onToggleDev(p.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {canEdit && (
        <button
          type="button"
          onClick={onCreate}
          data-testid="mod-tracker-pack-add"
          className="ml-1 inline-flex items-center gap-1.5 border border-dashed border-[#5C6B51] hover:border-[#C0A875] text-[#8D9A87] hover:text-[#C0A875] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.25em] transition-colors"
          title="Add a new pack (tab)"
        >
          + New pack
        </button>
      )}
      <span className="ml-auto font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">
        {packs.length} pack{packs.length === 1 ? "" : "s"}
      </span>
    </div>
  );
}

function PackTab({ pack, active, canEdit, canDelete, onSelect, onRename, onDelete, onToggleDev }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(pack.name || "");
  useEffect(() => setDraft(pack.name || ""), [pack.name]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pack.id, disabled: !canEdit });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  const commit = () => {
    const v = (draft || "").trim();
    if (v && v !== pack.name) onRename(v);
    setEditing(false);
  };

  const modCount = (pack.mods || []).length;
  const isDev = !!pack.exclude_from_duplicates;
  // Tooltip surfaces the pack description so admins can identify tabs at a
  // glance ("Guns, vests & ammo") without switching tabs.
  const descBits = [];
  if ((pack.description || "").trim()) descBits.push(pack.description);
  if (isDev) descBits.push("Dev pack — excluded from cross-pack duplicate check");
  const tooltip = descBits.length
    ? `${pack.name} — ${descBits.join(" · ")}${canEdit ? "\n(click to switch, double-click to rename)" : ""}`
    : canEdit
    ? "Click to switch pack, double-click to rename"
    : "Click to switch pack";

  // Dev packs get a distinct blue tint (both active and inactive) so
  // production packs remain the visual default (gold).
  const tabColor = isDev
    ? active
      ? "border-[#5D8FBF] bg-[#5D8FBF]/10 text-[#8DB4D9]"
      : "border-[#2A3B4A] text-[#6B8299] hover:border-[#5D8FBF]/60 hover:text-[#8DB4D9]"
    : active
    ? "border-[#C0A875] bg-[#C0A875]/10 text-[#C0A875]"
    : "border-[#283524] text-[#8D9A87] hover:border-[#5C6B51] hover:text-[#E2E4DF]";

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`mod-tracker-pack-tab-${pack.id}`}
      data-dev-pack={isDev ? "true" : "false"}
      className={`inline-flex items-center gap-1 border px-2 py-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] transition-colors ${tabColor}`}
    >
      {canEdit && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          title="Drag to reorder packs"
          data-testid={`mod-tracker-pack-drag-${pack.id}`}
          className="text-[#5C6B51] hover:text-[#C0A875] cursor-grab active:cursor-grabbing touch-none px-0.5"
        >
          <GripVertical size={11} />
        </button>
      )}
      {editing ? (
        <input
          type="text"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); setDraft(pack.name || ""); setEditing(false); }
          }}
          data-testid={`mod-tracker-pack-rename-input-${pack.id}`}
          className="bg-[#0D110C] border border-[#C0A875] text-[#E2E4DF] px-1.5 py-0.5 font-mono-ui text-[10px] uppercase tracking-[0.25em] focus:outline-none w-32"
        />
      ) : (
        <button
          type="button"
          onClick={onSelect}
          onDoubleClick={() => canEdit && setEditing(true)}
          data-testid={`mod-tracker-pack-select-${pack.id}`}
          className="px-1.5 py-0.5 flex items-center gap-1.5"
          title={tooltip}
        >
          {isDev && <FlaskConical size={11} className="opacity-80" />}
          <span>{pack.name}</span>
          <span className="text-[#5C6B51]">×{modCount}</span>
        </button>
      )}
      {canEdit && !editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          title="Rename pack"
          data-testid={`mod-tracker-pack-rename-${pack.id}`}
          className="text-[#5C6B51] hover:text-[#C0A875] px-0.5"
        >
          <Pencil size={10} />
        </button>
      )}
      {canEdit && !editing && (
        <button
          type="button"
          onClick={onToggleDev}
          title={
            isDev
              ? "Currently a DEV / TESTERS pack.\n• Excluded from the Production Mods= / Map= copy lines (has its own Dev section below).\n• Not scanned by the cross-pack duplicate warning.\nClick to flip back to production."
              : "Mark as DEV / TESTERS pack.\n• Removes its mods from the Production Mods= / Map= copy lines.\n• Skipped by the cross-pack duplicate warning.\nClick to flip."
          }
          data-testid={`mod-tracker-pack-toggle-dev-${pack.id}`}
          className={`px-1 py-0.5 border ${
            isDev
              ? "border-[#5D8FBF] bg-[#5D8FBF]/15 text-[#8DB4D9]"
              : "border-[#283524] text-[#5C6B51] hover:border-[#8DB4D9] hover:text-[#8DB4D9]"
          }`}
        >
          <FlaskConical size={11} />
        </button>
      )}
      {canDelete && !editing && (
        <button
          type="button"
          onClick={onDelete}
          title="Delete pack"
          data-testid={`mod-tracker-pack-delete-${pack.id}`}
          className="text-[#5C6B51] hover:text-[#C0392B] px-0.5"
        >
          <Trash2 size={10} />
        </button>
      )}
    </div>
  );
}
